import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'CompanyUserPicker' }

import { ref, watch, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
// Imported by source name so babel-plugin-component resolves the matching
// CSS as element-ui/lib/theme-chalk/autocomplete.css. Aliasing the import
// (e.g. `Autocomplete as ElAutocomplete`) makes the plugin look for
// el-autocomplete.css, which doesn't exist. Element UI's Autocomplete is
// not globally registered (see src/plugins/globalComponents.js); the
// template tag below uses <Autocomplete> so script-setup binds it.
import { Autocomplete } from 'element-ui'
import { API_LOCATION } from '@/api-config'

interface CompanyUser {
  id: number
  first_name: string
  last_name: string
  email: string
}

interface Suggestion extends CompanyUser {
  value: string
}

// Runtime prop declarations (not TS-only) so nullable props don't emit
// `type: [Object, null]` — Vue 2.7's SFC compiler turns `T | null` into a
// literal `null` in the type array, which Vue rejects as a non-constructor.

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  value: { type: Object as PropType<CompanyUser | null>, default: null },
  companyId: { type: [Number, String] as PropType<number | string | null>, default: null },
  companyGroupId: { type: [Number, String] as PropType<number | string | null>, default: null },
  placeholder: { type: String, default: 'Search staff...' },
  disabled: { type: Boolean, default: false },
  clearable: { type: Boolean, default: true },
  popperClass: { type: String, default: '' }
},
  emits: ["input", "select"],
  setup(__props, { emit }: { emit: ({
  (e: 'input', value: CompanyUser | null): void
  (e: 'select', value: CompanyUser): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





// Display value mirrors what existing call sites do — show the picked user's
// email in the input. Tracked locally so the user can still type to search.
const displayValue = ref(props.value?.email || '')

watch(
  () => props.value,
  (next) => {
    displayValue.value = next?.email || ''
  }
)

async function getAuthToken(): Promise<string> {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

async function search(queryString: string, cb: (results: Suggestion[]) => void) {
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const url =
      `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}` +
      `/company-users?count=10000000&autocomplete=${encodeURIComponent(queryString)}`
    const response = await axios.get(url, config)
    const users: CompanyUser[] = response.data.company_users || []
    const suggestions: Suggestion[] = users.map((u) => ({
      id: u.id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      // el-autocomplete uses `value` for its internal filter/display — keep
      // it as the email so the field reads cleanly post-select.
      value: u.email
    }))
    cb(suggestions)
  } catch (err) {
    console.error('Company user search failed:', err)
    cb([])
  }
}

function onInput(text: string) {
  displayValue.value = text
  // Backspacing the field to empty implicitly clears the picked user.
  if (!text && props.value) {
    emit('input', null)
  }
}

function onSelect(item: Suggestion) {
  const picked: CompanyUser = {
    id: item.id,
    first_name: item.first_name,
    last_name: item.last_name,
    email: item.email
  }
  displayValue.value = item.email
  emit('input', picked)
  emit('select', picked)
}

function onClear() {
  displayValue.value = ''
  emit('input', null)
}

return { __sfc: true,props, emit, displayValue, getAuthToken, search, onInput, onSelect, onClear, Autocomplete }
}

})